// ───────────────────────────────────────────────────────────────────────────
// 🔑 THE GUIDED LETTER OF EXPLANATION (approved by Hannah 2026-11-02 — backend constants/loxGuidedLetterCopy). It REPLACES
// the single free-text box everywhere a letter of explanation is asked for: the step-7 card (./Step7LetterAsks), a letter
// on step 7 and the step-7 view (./Step7Letter), a letter staff sent back (step 7's need card, the view's need row) and
// the dashboard's needs panel. Every word is the server's (`g`, GET /api/guided-flow/letter-copy).
//   · the questions, in order; the last one follows "Is it resolved?" (Yes → "Why won’t it happen again?", No / In
//     progress → "What are you doing to resolve it?") and appears once that is answered;
//   · no character counters: pressing "Review my letter" with a short answer shows "A few more words, please." under it;
//   · the review: the letter made from their answers (each question as asked, then the answer as typed — the server
//     assembles the very same letter and refuses any other), the confirm box UNTICKED, and "Send explanation" disabled
//     until it is ticked. The confirm is an attestation: Eva stays off while it is on screen (./attestation);
//   · onSend({ answers, confirmed: true, letterShown }) — the server records who ticked it, when, and this exact letter.
// ───────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { UPLOAD } from './Step5AskCard';
import { useAttestationOpen } from './attestation';

const API = process.env.REACT_APP_API_URL || '';

let cached = null;           // { guided } once read
let pending = null;
/** The guided letter's words (null while not approved, or when they cannot be read — the caller keeps its box). */
export function useGuidedLetterCopy(token) {
  const [copy, setCopy] = useState(cached ? cached.guided : undefined);
  useEffect(() => {
    if (cached) { setCopy(cached.guided); return undefined; }
    let live = true;
    pending = pending || axios.get(`${API}/api/guided-flow/letter-copy`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then((r) => { cached = { guided: (r.data && r.data.guided) || null }; return cached; })
      .catch(() => { pending = null; return { guided: null }; });
    pending.then((c) => { if (live) setCopy(c.guided); });
    return () => { live = false; };
  }, [token]);
  return copy;   // undefined while loading
}

/** The label a question is asked with (the last follows "Is it resolved?"). */
export const labelOf = (g, f, a) => (f.labelByResolved ? (a.resolved === 'Yes' ? g.againLabel : g.resolveLabel) : f.label);
/** The letter, exactly as the server assembles it (constants/loxGuidedLetterCopy assembleLetter). */
export function guidedLetterOf(g, a) {
  const show = (f) => (f.kind === 'month_year' ? `${g.months[Number(a.when && a.when.month) - 1] || ''} ${(a.when && a.when.year) || ''}`.trim() : String(a[f.key] || '').trim());
  return g.fields.map((f) => `${labelOf(g, f, a)}\n${show(f)}`).join('\n\n');
}
/** The questions not yet answered well enough (text under its minimum, no month/year, no choice). */
export function guidedMissing(g, a) {
  return g.fields.filter((f) => (f.kind === 'text' ? String(a[f.key] || '').trim().length < f.min
    : f.kind === 'choice' ? !f.options.includes(a[f.key]) : !(a.when && Number(a.when.month) >= 1 && Number(a.when.year) >= 1950))).map((f) => f.key);
}

const FIELD = { width: '100%', boxSizing: 'border-box', fontSize: 13, padding: 8, borderRadius: 8, border: '1px solid #d1d5db', fontFamily: 'inherit', background: '#fff' };
const BAD = '1px solid #7f1d1d';
const plain = (off) => ({ ...UPLOAD, marginLeft: 0, background: off ? '#f3f4f6' : '#fff', color: off ? '#9ca3af' : '#000', cursor: off ? 'not-allowed' : 'pointer' });

function Review({ children }) {
  useAttestationOpen();
  return children;
}

/**
 * @param {object} p
 * @param {object} p.g                the server's copy
 * @param {string|null} p.busy        'text' while sending
 * @param {function} p.onSend         ({ answers, confirmed, letterShown }) → Promise
 * @param {React.ReactNode} [p.uploadButton]  "Upload signed letter", where the surface offers it
 * @param {string} [p.testid]
 */
export default function GuidedLetter({ g, busy, onSend, uploadButton = null, testid = 'guided-step7-letter-guided' }) {
  const [a, setA] = useState({ when: {} });
  const [step, setStep] = useState('fields');   // 'fields' | 'review'
  const [confirmed, setConfirmed] = useState(false);
  const [tried, setTried] = useState(false);
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const missing = guidedMissing(g, a);
  const year = new Date().getFullYear();
  const years = Array.from({ length: 31 }, (_, i) => year - i);
  if (step === 'review') {
    const letter = guidedLetterOf(g, a);
    const off = !confirmed || !!busy;
    return (
      <Review>
        <div data-testid="guided-step7-letter-review" style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: '#000' }}>{g.reviewHeading}</div>
          <div style={{ fontSize: 13, color: '#000' }}>{g.reviewIntro}</div>
          <div data-testid="guided-step7-letter-review-text" style={{ ...FIELD, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.6 }}>{letter}</div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#000', cursor: 'pointer' }}>
            <input type="checkbox" data-testid="guided-step7-letter-confirm" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{g.confirm}</span>
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button type="button" data-testid="guided-step7-letter-edit" disabled={!!busy} onClick={() => { setStep('fields'); setConfirmed(false); }} style={plain(!!busy)}>{g.editButton}</button>
            <button type="button" data-testid="guided-step7-letter-ask-send" disabled={off}
              onClick={() => { if (!off) onSend({ answers: { ...a, when: { month: Number(a.when.month), year: Number(a.when.year) } }, confirmed: true, letterShown: letter }); }}
              style={plain(off)}>
              {busy === 'text' ? 'Sending…' : g.sendButton}
            </button>
          </div>
        </div>
      </Review>
    );
  }
  const short = (f) => tried && missing.includes(f.key);
  return (
    <div data-testid={testid} style={{ display: 'grid', gap: 10 }}>
      {g.fields.filter((f) => !f.labelByResolved || (a.resolved && g.fields.find((x) => x.key === 'resolved').options.includes(a.resolved))).map((f) => (
        <div key={f.key} data-testid={`guided-step7-letter-field-${f.key}`} data-short={short(f) ? 'true' : 'false'} style={{ display: 'grid', gap: 4 }}>
          <label htmlFor={`lox-guided-${f.key}`} style={{ fontSize: 13, fontWeight: 700, color: '#000' }}>{labelOf(g, f, a)}</label>
          {f.kind === 'text' ? (
            <textarea id={`lox-guided-${f.key}`} rows={2} value={a[f.key] || ''} disabled={!!busy} onChange={(e) => set(f.key, e.target.value)}
              aria-invalid={short(f)} style={{ ...FIELD, resize: 'vertical', ...(short(f) ? { border: BAD } : {}) }} />
          ) : f.kind === 'month_year' ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select id={`lox-guided-${f.key}`} aria-label={g.monthPlaceholder} aria-invalid={short(f)} value={a.when.month || ''} disabled={!!busy}
                onChange={(e) => set('when', { ...a.when, month: e.target.value })} style={{ ...FIELD, width: 'auto', minWidth: 140, ...(short(f) && !a.when.month ? { border: BAD } : {}) }}>
                <option value="">{g.monthPlaceholder}</option>
                {g.months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select aria-label={g.yearPlaceholder} aria-invalid={short(f)} value={a.when.year || ''} disabled={!!busy}
                onChange={(e) => set('when', { ...a.when, year: e.target.value })} style={{ ...FIELD, width: 'auto', minWidth: 110, ...(short(f) && !a.when.year ? { border: BAD } : {}) }}>
                <option value="">{g.yearPlaceholder}</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          ) : (
            <div role="radiogroup" aria-label={f.label} aria-invalid={short(f)} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {f.options.map((o) => (
                <button key={o} type="button" role="radio" aria-checked={a[f.key] === o} disabled={!!busy} onClick={() => set(f.key, o)}
                  style={{ ...UPLOAD, marginLeft: 0, background: a[f.key] === o ? '#f3f4f6' : '#fff', fontWeight: a[f.key] === o ? 800 : 600, ...(short(f) ? { border: BAD } : {}) }}>{o}</button>
              ))}
            </div>
          )}
          {/* 🔑 No counter (Hannah): a short answer, once they press "Review my letter", gets the one line. */}
          {short(f) && f.kind === 'text' ? <div data-testid={`guided-step7-letter-more-${f.key}`} style={{ fontSize: 12, color: '#7f1d1d' }}>{g.moreWords}</div> : null}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {uploadButton}
        <button type="button" data-testid="guided-step7-letter-review-button" disabled={!!busy}
          onClick={() => { setTried(true); if (!missing.length) setStep('review'); }} style={plain(!!busy)}>
          {g.reviewButton}
        </button>
      </div>
    </div>
  );
}

